import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconPlus } from "../../components/icons";
import {
  BILAG_MAKS_ANTAL,
  BILAG_MAKS_BYTES,
  BILAG_TYPER,
  type EgenAftale,
  type EgenAftaleVedhaeft,
} from "../../lib/api";

const tom: EgenAftale = {
  navn: "",
  kommentar: "",
  prismodel: "spot",
  fastprisKrKwh: 0,
  kwhTillaeg: 0,
  maanedsabonnement: 0,
  udeprisKrKwh: 0,
  ladeboksInkluderet: false,
  boksPris: 0,
  oprettelsesGebyr: 0,
  bindingsperiodeMdr: 0,
};

/** Collapsible "add your own agreement" card, slotted into the results list.
 * No outer margin — the list's own gap does the spacing. */
export default function EgenAftaleBox({
  egen,
  onSave,
  onRemove,
}: {
  egen: EgenAftale | null;
  onSave: (e: EgenAftale, v: EgenAftaleVedhaeft) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<EgenAftale>(egen ?? tom);
  const set = <K extends keyof EgenAftale>(k: K, v: EgenAftale[K]) => setForm({ ...form, [k]: v });

  // Terms + files are sent to us but never stored in the browser, so they live
  // here rather than in the persisted EgenAftale.
  const [vilkaarTekst, setVilkaarTekst] = useState("");
  const [filer, setFiler] = useState<File[]>([]);
  const [filFejl, setFilFejl] = useState<string | null>(null);
  const filInput = useRef<HTMLInputElement>(null);

  const tilfoejFiler = (valgte: FileList | null) => {
    if (!valgte?.length) return;
    const forStore = [...valgte].filter((f) => f.size > BILAG_MAKS_BYTES);
    const nye = [...valgte].filter((f) => f.size <= BILAG_MAKS_BYTES);
    const samlet = [...filer, ...nye].slice(0, BILAG_MAKS_ANTAL);
    setFiler(samlet);
    setFilFejl(
      forStore.length > 0
        ? t("egen.filForStor", { maks: BILAG_MAKS_BYTES / (1024 * 1024) })
        : filer.length + nye.length > BILAG_MAKS_ANTAL
          ? t("egen.filForMange", { maks: BILAG_MAKS_ANTAL })
          : null,
    );
    if (filInput.current) filInput.current.value = ""; // so the same file can be re-picked
  };

  if (!open)
    return (
      <div className="rounded-l border border-dashed border-line-strong bg-surface-sunk px-5 py-4 text-center">
        <p className="text-[13.5px] text-ink-soft">{t("egen.intro")}</p>
        <button type="button" className="btn btn-ghost mt-2" onClick={() => setOpen(true)}>
          {!egen && <IconPlus className="h-[17px] w-[17px]" />}
          {egen ? t("egen.edit") : t("egen.cta")}
        </button>
      </div>
    );

  return (
    <form
      className="rounded-l border border-line-strong bg-surface p-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form, { vilkaarTekst, filer });
        setOpen(false);
      }}
    >
      <h3 className="font-display text-xl">{t("egen.title")}</h3>
      <p className="mt-1 text-[13px] text-ink-soft">{t("egen.help")}</p>

      <div className="mt-4 flex flex-wrap items-end gap-x-5 gap-y-3">
        <label className="flex flex-col gap-1">
          <Cap>{t("egen.navn")}</Cap>
          <input
            required
            maxLength={100}
            className="w-52 rounded-s border border-line bg-canvas-2 px-3 py-2 text-sm"
            value={form.navn}
            onChange={(e) => set("navn", e.target.value)}
            placeholder={t("egen.navnPh")}
          />
        </label>
        <label className="flex flex-col gap-1">
          <Cap>{t("detail.prismodel")}</Cap>
          <select
            className="rounded-s border border-line bg-canvas-2 px-2 py-2 text-sm"
            value={form.prismodel}
            onChange={(e) => set("prismodel", e.target.value as "spot" | "fast")}
          >
            <option value="spot">{t("detail.spot")}</option>
            <option value="fast">{t("detail.fast")}</option>
          </select>
        </label>
        {form.prismodel === "spot" ? (
          <Num label={t("egen.tillaeg")} value={form.kwhTillaeg} onChange={(v) => set("kwhTillaeg", v)} />
        ) : (
          <Num label={t("egen.fastpris")} value={form.fastprisKrKwh} onChange={(v) => set("fastprisKrKwh", v)} />
        )}
        <Num label={t("egen.abon")} value={form.maanedsabonnement} onChange={(v) => set("maanedsabonnement", v)} />
        <Num label={t("egen.udepris")} value={form.udeprisKrKwh} onChange={(v) => set("udeprisKrKwh", v)} />
        <label className="flex flex-col items-center gap-1">
          <Cap>{t("egen.boksInkl")}</Cap>
          <input
            type="checkbox"
            className="h-5 w-5 accent-[var(--accent)]"
            checked={form.ladeboksInkluderet}
            onChange={(e) => set("ladeboksInkluderet", e.target.checked)}
          />
        </label>
        {!form.ladeboksInkluderet && (
          <Num label={t("egen.boksPris")} value={form.boksPris} onChange={(v) => set("boksPris", v)} />
        )}
        <Num label={t("egen.oprettelse")} value={form.oprettelsesGebyr} onChange={(v) => set("oprettelsesGebyr", v)} />
        <Num label={t("egen.binding")} value={form.bindingsperiodeMdr} onChange={(v) => set("bindingsperiodeMdr", Math.round(v))} />
      </div>

      <label className="mt-3 flex flex-col gap-1">
        <Cap>{t("egen.kommentar")}</Cap>
        <input
          maxLength={500}
          className="rounded-s border border-line bg-canvas-2 px-3 py-2 text-sm"
          value={form.kommentar}
          onChange={(e) => set("kommentar", e.target.value)}
          placeholder={t("egen.kommentarPh")}
        />
      </label>

      <label className="mt-3 flex flex-col gap-1">
        <Cap>{t("egen.vilkaar")}</Cap>
        <textarea
          rows={4}
          maxLength={20000}
          className="rounded-s border border-line bg-canvas-2 px-3 py-2 text-sm"
          value={vilkaarTekst}
          onChange={(e) => setVilkaarTekst(e.target.value)}
          placeholder={t("egen.vilkaarPh")}
        />
      </label>

      <div className="mt-3 flex flex-col gap-1">
        <Cap>{t("egen.bilag")}</Cap>
        <p className="text-[12.5px] text-ink-soft">{t("egen.bilagHelp")}</p>
        <input
          ref={filInput}
          type="file"
          multiple
          accept={BILAG_TYPER}
          className="mt-1 max-w-full text-[13px] file:mr-3 file:rounded-s file:border file:border-line file:bg-canvas-2 file:px-3 file:py-1.5 file:text-[13px] file:text-ink"
          onChange={(e) => tilfoejFiler(e.target.files)}
        />
        {filFejl && <p className="text-[12.5px] text-danger">{filFejl}</p>}
        {filer.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1">
            {filer.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-2 font-mono text-[12px] text-ink-soft">
                <span className="truncate">{f.name}</span>
                <span className="text-ink-faint">{Math.max(1, Math.round(f.size / 1024))} kB</span>
                <button
                  type="button"
                  className="text-danger"
                  aria-label={t("egen.filFjern")}
                  onClick={() => setFiler(filer.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="submit" className="btn btn-primary">{t("egen.save")}</button>
        {egen && (
          <button type="button" className="btn btn-ghost" onClick={() => { onRemove(); setForm(tom); setOpen(false); }}>
            {t("egen.remove")}
          </button>
        )}
        <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>{t("egen.cancel")}</button>
      </div>
      <p className="mt-3 font-mono text-[11px] text-ink-faint">{t("egen.note")}</p>
    </form>
  );
}

const Cap = ({ children }: { children: React.ReactNode }) => (
  <span className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">{children}</span>
);

function Num({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <Cap>{label}</Cap>
      <input
        type="number"
        step="0.01"
        min="0"
        className="w-28 rounded-s border border-line bg-canvas-2 px-2 py-2 font-mono text-sm"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
      />
    </label>
  );
}
